import { useAuth } from "@dl/web-core-js/auth";
import {
  columns as defineColumns,
  DataTable,
  type RowEditorConfig,
} from "@dl/web-core-js/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  LazyProductPricingClient,
  type LazyProductPricingDTO,
} from "../../api/pricing";
import { fxRate, ReportingClient } from "../../api/reporting";
import { basePricingColumns, round2 } from "./pricingShared";

/**
 * Pricing → Product rules: per-product gross-margin overrides. The suggested
 * price is `cogs / (1 − margin) / fx` (with MSRP shown at twice that), using
 * the override when one exists and the global GROSS_MARGIN rule otherwise.
 * Editing the override to a non-zero value creates or updates the rule.
 */
export function ProductPricingRuleGrid({ toolbar }: { toolbar: ReactNode }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();

  const pricing = useMemo(() => new LazyProductPricingClient(http), [http]);
  const reporting = useMemo(() => new ReportingClient(http), [http]);

  const { data, isLoading } = useQuery({
    queryKey: ["pricing", "rules"],
    queryFn: async () => {
      const [rows, fx, globalRules] = await Promise.all([
        pricing.byTypeAndStatus(
          ["SINGLE_ITEM"],
          ["AVAILABLE", "PENDING_DISCONTINUED", "PENDING_LAUNCH"],
        ),
        reporting.globalFx(),
        http.get<Array<{ id: number; type: string; value: number }>>("/global/pricing/rule"),
      ]);
      const effective = await pricing.effectiveCogs(rows.map((row) => row.id));
      const globalMargin = globalRules.find((rule) => rule.type === "GROSS_MARGIN")?.value ?? 0;
      return { rows, fx, effective, globalMargin };
    },
    staleTime: 5 * 60_000,
  });

  const cols = useMemo(() => {
    const c = defineColumns<LazyProductPricingDTO>();
    const effective = data?.effective ?? {};
    const globalMargin = data?.globalMargin ?? 0;

    const suggested = (row: LazyProductPricingDTO, currency: "CAD" | "USD") => {
      const rate = data ? fxRate(data.fx, currency) : 1;
      const cogs = effective[String(row.id)] ?? row.pricing?.cogs ?? 0;
      const margin = (row.pricing_rule?.gross_margin ?? globalMargin) / 100;
      return margin >= 1 || rate === 0 ? 0 : cogs / (1 - margin) / rate;
    };
    const priceCell = (row: LazyProductPricingDTO, currency: "CAD" | "USD") => {
      const price = suggested(row, currency);
      return (
        <span className="tabular">
          {price.toFixed(2)}
          <span className="ml-1 text-[11px] text-ink-faint">MSRP {(price * 2).toFixed(2)}</span>
        </span>
      );
    };

    return [
      ...basePricingColumns(c),
      c.text("category", (row) => row.category?.name ?? "", {
        headerKey: "column.category",
        filter: "select",
      }),
      c.number("weekly_avg", (row) => round2(row.kpi_sales?.velocity), {
        headerKey: "pricing.weekly-avg",
      }),
      c.number("sales_rank", (row) => row.kpi_sales?.sales_rank ?? null, {
        headerKey: "column.sales-rank",
        defaultVisible: false,
      }),
      c.custom(
        "sales_ytd",
        (row) => (row.kpi_sales?.ytd_units_cad ?? 0) + (row.kpi_sales?.ytd_units_usd ?? 0),
        (row) => (
          <span className="tabular">
            {(row.kpi_sales?.ytd_units_cad ?? 0) + (row.kpi_sales?.ytd_units_usd ?? 0)}
            <span className="ml-1 text-[11px] text-ink-faint">
              {Math.round(row.kpi_sales?.ytd_sales_cad ?? 0)} CAD ·{" "}
              {Math.round(row.kpi_sales?.ytd_sales_usd ?? 0)} USD
            </span>
          </span>
        ),
        { headerKey: "pricing.sales-ytd", defaultVisible: false },
      ),
      c.custom(
        "suggested_cad",
        (row) => Math.round(suggested(row, "CAD") * 100) / 100,
        (row) => priceCell(row, "CAD"),
        { headerKey: "pricing.suggested-cad", priority: 3, filter: "none" },
      ),
      c.custom(
        "suggested_usd",
        (row) => Math.round(suggested(row, "USD") * 100) / 100,
        (row) => priceCell(row, "USD"),
        { headerKey: "pricing.suggested-usd", filter: "none" },
      ),
      c.custom(
        "gross_margin",
        () => globalMargin,
        () => <span className="tabular text-ink-muted">{globalMargin.toFixed(1)}%</span>,
        { headerKey: "pricing.global-margin", filter: "none", enableSorting: false },
      ),
      c.custom(
        "gross_margin_override",
        (row) => row.pricing_rule?.gross_margin ?? 0,
        (row) =>
          row.pricing_rule ? (
            <span className="tabular font-medium">{row.pricing_rule.gross_margin.toFixed(1)}%</span>
          ) : (
            <span className="text-ink-faint"></span>
          ),
        { headerKey: "pricing.margin-override", priority: 3 },
      ),
    ];
  }, [data]);

  const editor = useMemo<RowEditorConfig<LazyProductPricingDTO>>(
    () => ({
      fields: {
        gross_margin_override: {
          kind: "number",
          get: (row) => row.pricing_rule?.gross_margin ?? 0,
          set: (row, value) => {
            const margin = value === "" ? 0 : Number(value);
            if (!row.pricing_rule && margin !== 0) {
              return { ...row, pricing_rule: { id: 0, gross_margin: margin } };
            }
            if (row.pricing_rule) {
              return { ...row, pricing_rule: { ...row.pricing_rule, gross_margin: margin } };
            }
            return row;
          },
        },
      },
      validate: (row): Record<string, string> | null => {
        const margin = row.pricing_rule?.gross_margin ?? 0;
        // The Vaadin editor bounds: 10–100 when a rule exists (0 clears nothing).
        if (margin !== 0 && (margin < 10 || margin > 100)) {
          return {
            gross_margin_override: t("pricing.margin-range", "Margin must be between 10 and 100"),
          };
        }
        return null;
      },
      onSave: async (row) => {
        await pricing.update(row);
        await queryClient.invalidateQueries({ queryKey: ["pricing"] });
        toast.success(t("notification.saved", "Saved"));
      },
    }),
    [pricing, queryClient, t],
  );

  return (
    <>
      <DataTable
        data={data?.rows ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        toolbarExtra={toolbar}
        loading={isLoading}
        editor={editor}
        emptyMessage={t("table.empty", "Nothing to show")}
      />
    </>
  );
}
